import React, { useMemo, useState } from "react";
import { Chart } from "react-google-charts";
import { EtudeTypes } from "components/EtudeForm/constants";
import useTranslation from "utils/hooks/useTranslation";

type Props = {
    solution?: Solution | null;
    json?: any;
    service?: string;
    locationInfo?: GetLocationInfoResponse | null;
};

/**
 * Converts a Record<number, number> (keyed by temperature) into a sorted
 * Map<number, number> so we can iterate by temperature.
 */
const recordToMap = (rec?: Record<number, number> | null): Map<number, number> => {
    const map = new Map<number, number>();
    if (!rec || typeof rec !== "object") return map;
    Object.keys(rec)
        .map(Number)
        .filter(Number.isFinite)
        .sort((a, b) => a - b)
        .forEach((k) => {
            const v = Number(rec[k]);
            if (Number.isFinite(v)) map.set(k, v);
        });
    return map;
};

export const SolutionGraphChart: React.FC<Props> = ({ solution, json, service, locationInfo: locationInfoProp }) => {
    const { t } = useTranslation();
    const [showTbaseTooltip, setShowTbaseTooltip] = useState(false);
    const hasHeating = service?.includes(EtudeTypes.chauffage) ?? false;
    const hasEcs = service?.toLocaleLowerCase().includes(EtudeTypes.ECS) ?? false;
    const hasTcoupure = !!(json?.TcoupureQuestion && json?.Tcoupure != null);
    const showCPoint = !hasTcoupure && !(hasEcs && !hasHeating);

    const graph = useMemo(() => {
        // --- Resolve localization data (jour_an_temp, jour_an_temp_ete) ---
        const locationInfo: GetLocationInfoResponse | undefined =
            locationInfoProp ?? json?.locationInfo ?? json?.localisation ?? json?.villeData;

        const jourAnTemp = recordToMap(locationInfo?.jour_an_temp);
        const jourAnTempEte = recordToMap(locationInfo?.jour_an_temp_ete);

        // --- Resolve solution graph data ---
        const graphique = solution?.graphique ?? (json?.selectedSolution as Solution)?.graphique;

        const deperChauffage = recordToMap(graphique?.deper_chauffage);
        const deperEcs = recordToMap(graphique?.deper_ecs);
        const puissancePac = recordToMap(graphique?.puissance_pac);

        // --- Determine temperature axis from all available keys ---
        const allTemps = new Set<number>();
        for (const k of jourAnTemp.keys()) allTemps.add(k);
        for (const k of jourAnTempEte.keys()) allTemps.add(k);
        for (const k of deperChauffage.keys()) allTemps.add(k);
        for (const k of deperEcs.keys()) allTemps.add(k);
        for (const k of puissancePac.keys()) allTemps.add(k);

        // --- Tbase or Tcoupure ---

        const tBaseOrCut: number | undefined = hasTcoupure
            ? Number(json?.Tcoupure)
            : json?.t_base != null
                ? Number(json.t_base)
                : undefined;

        // --- Bivalence point (C) ---
        const solutionAny = solution as any;
        const tBivalence: number | undefined = Number(
            solutionAny?.t_bivalence
        ) || undefined;
        const pBivalence: number | undefined = Number(
            solutionAny?.p_bivalence
        ) || undefined;

        // --- Insert exact decimal temperatures for points so they appear on the axis ---
        if (tBaseOrCut != null && Number.isFinite(tBaseOrCut)) allTemps.add(tBaseOrCut);
        if (tBivalence != null && Number.isFinite(tBivalence)) allTemps.add(tBivalence);

        const temperatures = [...allTemps].sort((a, b) => a - b);

        if (temperatures.length === 0) {
            return { rows: [] as any[][], hasData: false };
        }

        // --- Build header and rows ---
        // Columns: Temperature | WinterHist | SummerHist | HeatingNeed | DHWNeed | HPPower | PointA | A-Label | PointB | B-Label | PointC | C-Label
        const rows: (string | number | null)[][] = temperatures.map((temp) => {
            let tempLabel = Number.isInteger(temp) ? temp : Math.round(temp);
            const winter = jourAnTemp.get(temp) ?? null;
            const summer = jourAnTempEte.get(temp) ?? null;

            const heating = hasHeating ? (deperChauffage.get(temp) ?? null) : null;
            const dhw = hasEcs ? (deperEcs.get(temp) ?? null) : null;
            const hpPower = puissancePac.get(temp) ?? null;
            // Point A: puissance_pac at Tbase/Tcoupure
            let pointA = tBaseOrCut != null && temp === tBaseOrCut ? (Math.round(puissancePac.get(temp) ?? 0)) : null;
            // Point B: deper_chauffage at Tbase/Tcoupure
            let pointB = tBaseOrCut != null && temp === tBaseOrCut && hasHeating ? (Math.round(deperChauffage.get(temp) ?? 0)) : null;
            // Point C: p_bivalence at t_bivalence
            const pointC = tBivalence != null && temp === Math.round(tBivalence) && pBivalence != null ? Math.round(pBivalence) : null;
            let pointALabel = `A  ${tempLabel}, ${pointA}`;
            let pointBLabel = `B  ${tempLabel}, ${pointB}`;
            let pointCLabel = `C  ${tempLabel}, ${pointC}`;
            const maxVertical = Math.max(...[heating, dhw, hpPower, pointA, pointB].filter((v): v is number => v != null));
            const sumCount = maxVertical > 40 ? (maxVertical > 60 ? 5 : 3) : 2;
            if (pointA && pointA == pointB) {
                pointA += sumCount;
            }

            // Offset pointC if it overlaps with pointB or pointA at the same temperature
            let pointCAdjusted = pointC;
            if (pointCAdjusted != null) {

                if (pointCAdjusted == pointB) pointCAdjusted -= sumCount;
                if (pointCAdjusted == pointA) pointCAdjusted -= sumCount;
            }


            return [
                tempLabel,
                winter,
                summer,
                heating,
                dhw,
                hpPower,
                pointA,
                pointA != null ? "A" : null,
                pointALabel,
                pointB,
                pointB != null ? "B" : null,
                pointBLabel,
                ...(showCPoint ? [pointCAdjusted, pointCAdjusted != null ? "C" : null, pointCLabel] : []),
            ];
        });

        const tBaseIndex =
            tBaseOrCut == null
                ? -1
                : temperatures.findIndex((temp) => temp === tBaseOrCut);

        return {
            rows,
            hasData: true,
            tBaseIndex,
            tempCount: temperatures.length,
            tBaseValue: tBaseOrCut,
            tBaseLabel: hasTcoupure ? t("FO_DETAILS_GRAPH_LEG_TCOUPURE") : t("FO_DETAILS_GRAPH_LEG_TBASE"),
            temperatures,
        };
    }, [json, service, solution, locationInfoProp]);
    if (!graph.hasData) {
        return <group data-space="20">{t("FO_INFO_GRAPH")}</group>;
    }

    const header: any[] = [
        { type: "number", label: t("FO_GRAPH_FUNCTIONING_Y") },
        { type: "number", label: t("FO_DETAILS_GRAPH_LEG_TEMP") },
        { type: "number", label: t("FO_DETAILS_GRAPH_LEG_TEMP_ETE") },
        { type: "number", label: t("FO_DETAILS_GRAPH_LEG_HEATING_NEED") },
        { type: "number", label: t("FO_DETAILS_GRAPH_LEG_DHW_NEED") },
        { type: "number", label: t(hasHeating ? "FO_DETAILS_GRAPH_LEG_POWER" : " FO_DETAILS_GRAPH_LEG_POWER_ECS") },
        { type: "number", label: "A" },
        { type: "string", role: "annotation" } as any,
        { type: "string", role: "tooltip", p: { html: true } },
        { type: "number", label: "B" },
        { type: "string", role: "annotation" } as any,
        { type: "string", role: "tooltip" },
        ...(showCPoint ? [
            { type: "number", label: "C" },
            { type: "string", role: "annotation" } as any,
            { type: "string", role: "tooltip" },
        ] : []),
    ] as any[];

    const chartData: any[] = [header, ...graph.rows];

    const customLegendItems = [
        { label: t("FO_DETAILS_GRAPH_LEG_TEMP"), color: "#2a6fdb", show: true },
        { label: t("FO_DETAILS_GRAPH_LEG_TEMP_ETE"), color: "#f4c430", show: true },
        { label: t(hasTcoupure ? "FO_DETAILS_GRAPH_LEG_TCOUPURE" : "FO_DETAILS_GRAPH_LEG_TBASE"), color: "#6f42c1", show: true, dashed: true },
        { label: t("FO_DETAILS_GRAPH_LEG_HEATING_NEED"), color: "#f57c00", show: hasHeating },
        { label: t("FO_DETAILS_GRAPH_LEG_DHW_NEED"), color: "#00a152", show: hasEcs },
        { label: t(hasHeating ? "FO_DETAILS_GRAPH_LEG_POWER" : "FO_DETAILS_GRAPH_LEG_POWER_ECS"), color: "#d32f2f", show: true },
    ].filter((item) => item.show);

    const tBaseLineLeftPercent =
        graph.tBaseIndex != null && graph.tBaseIndex >= 0 && graph.tempCount > 0
            ? ((graph.tBaseIndex + 0.5) / graph.tempCount) * 100
            : null;
    return (
        <group data-length="auto" data-min-height="520" >
            <group data-length="auto" data-min-height="520" data-position="relative">
                <Chart
                    chartType="ComboChart"
                    width="100%"
                    height="520px"
                    data={chartData}
                    index={100}
                    options={{
                        interpolateNulls: true,
                        chartArea: { left: 90, right: 90, top: 30, bottom: 60 },
                        hAxis: {
                            title: t("FO_GRAPH_FUNCTIONING_Y"),
                            // ticks: graph.temperatures,
                            format: '#', // Ensures whole numbers with no decimals
                            gridlines: {
                                step: 1 // Tells the chart to count by 1s (11, 12, 13...) instead of 10s
                            },
                        },
                        vAxes: {
                            0: { title: t("FO_GRAPH_FUNCTIONING_X1"), minValue: 0 },
                            1: { title: t("FO_GRAPH_FUNCTIONING_X2"), minValue: 0 },
                        },
                        seriesType: "bars",
                        series: {
                            // 0 = Winter histogram (right axis - hours)
                            0: { type: "bars", targetAxisIndex: 1, color: "#2a6fdb" },
                            // 1 = Summer histogram (right axis - hours)
                            1: { type: "bars", targetAxisIndex: 1, color: "#f4c430" },
                            // 2 = Heating need curve (left axis)
                            2: { type: "line", targetAxisIndex: 0, color: "#f57c00", lineWidth: 1, pointSize: 0 },
                            // 3 = DHW need curve (left axis)
                            3: { type: "line", targetAxisIndex: 0, color: "#00a152", lineWidth: 1, pointSize: 0 },
                            // 4 = HP Power curve (left axis)
                            4: { type: "line", targetAxisIndex: 0, color: "#d32f2f", lineWidth: 1, pointSize: 0 },
                            // 5 = Point A marker
                            5: { type: "line", targetAxisIndex: 0, color: "#d32f2f", pointSize: 27, lineWidth: 0, visibleInLegend: false },
                            // 6 = Point B marker
                            6: { type: "line", targetAxisIndex: 0, color: "#f57c00", pointSize: 27, lineWidth: 0, visibleInLegend: false },
                            // 7 = Point C marker (only when no Tcoupure)
                            ...(showCPoint ? { 7: { type: "line", targetAxisIndex: 0, color: "#00695c", pointSize: 27, lineWidth: 0, visibleInLegend: false } } : {}),
                        },
                        annotations: {
                            stem: { color: "transparent", length: 0 },
                            textStyle: {
                                color: "#ffffff",
                                fontSize: 13,
                                bold: false,
                                auraColor: "none",
                                // auraColor: "#d32f2f",
                            },
                            alwaysOutside: false,
                        },
                        legend: "none",
                        bar: { groupWidth: '54%' },
                        isStacked: true,
                        // tooltip: {
                        //     isHtml: true,
                        // }
                        // histogram: {
                        //     bucketSize: 0.1
                        // }
                    }}
                />
                {tBaseLineLeftPercent != null && (
                    <div
                        style={{
                            position: "absolute",
                            top: 30,
                            bottom: 60,
                            left: 90,
                            right: 90,
                            pointerEvents: "none",
                        }}
                    >
                        <div
                            onMouseEnter={() => setShowTbaseTooltip(true)}
                            onMouseLeave={() => setShowTbaseTooltip(false)}
                            style={{
                                position: "absolute",
                                left: `${tBaseLineLeftPercent}%`,
                                top: 0,
                                bottom: 0,
                                borderLeft: "2px dashed #6f42c1",
                                transform: "translateX(-1px)",
                                pointerEvents: "auto",
                                cursor: "pointer",
                                zIndex: 1,
                            }}
                        />
                        {showTbaseTooltip && (
                            <group
                                // data-min-length="120"
                                // data-max-length="350"
                                data-width="auto"
                                data-text-size="12"
                                data-space="8"
                                data-position="absolute"
                                data-color="black"
                                style={{
                                    left: `${tBaseLineLeftPercent}%`,
                                    top: -37,
                                    transform: "translateX(-50%)",
                                    whiteSpace: "nowrap",
                                    pointerEvents: "none",
                                    border: "1px groove #b4b4b4",
                                    zIndex: 100,
                                }}
                            >
                                {`${graph.tBaseLabel}: `}&nbsp;<b>{` ${graph.tBaseValue}°C`}</b>
                            </group>
                        )}
                    </div>
                )}
            </group>
            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px 16px",
                    alignItems: "center",
                    marginTop: "12px",
                    padding: "0 12px",
                }}
            >
                {customLegendItems.map((item) => (
                    <div
                        key={item.label}
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            minWidth: "180px",
                        }}
                    >
                        <span
                            style={{
                                width: "22px",
                                borderTop: item.dashed
                                    ? `2px dashed ${item.color}`
                                    : `4px solid ${item.color}`,
                                display: "inline-block",
                            }}
                        />
                        <span style={{ fontSize: "12px", lineHeight: 1.2 }}>{item.label}</span>
                    </div>
                ))}
            </div>
        </group >
    );
};
